import { NineSliceButton, fonts, Focusable, FocusManager } from '../../ui';
import { CoordinateKeypad, CLEAR } from './CoordinateKeypad';
import { LoadContext } from '../../utils';
import { Settings } from '../../settings';

const FRAME_WIDTH = 5;

export class CoordinateEntry extends NineSliceButton
{
    private allowedLabels: string[];
    public tutorialBlocked: boolean;
    public nextFocus: Focusable;
    public changeCB?: (wasFocused:boolean) => Promise<void>;

    public static preload()
    {
        const loader = LoadContext.current;
        loader.add('GameAtlas', './assets/game/GameAtlas{size}.json');
        super.preload();
    }

    constructor(labels:string[])
    {
        const loader = LoadContext.current;
        const width = 84;
        const height = 75;
        super(
            {
                texture: loader.getSpritesheet('GameAtlas').textures['coord_bg'],
                frame: FRAME_WIDTH,
                width,
                height,
            },
            {
                content: '',
                style: fonts.GameCoordButton
            }
        );
        this.allowedLabels = labels;
        this.tutorialBlocked = false;
        this.nextFocus = null;

        this.enabled = true;

        this.on('pointertap', async () => {
            if (!this.enabled) return;
            // if blocked by tutorial
            if (this.tutorialBlocked)
            {
                this.emit('tutorialBlocked');
                return;
            }
            const openPos = {
                x: 0,
                y: height / 2 + 10
            };
            // non-event taps will happen in hovered dwell clicks, and we don't want to change
            // focus in that situation.
            let wasFocused = FocusManager.instance.focused === this;
            const result = await CoordinateKeypad.instance.open(this.allowedLabels, this.toGlobal(openPos as any, openPos as any));
            if (result === CLEAR)
            {
                this.clear();
            }
            else if (result)
            {
                this.label = result;
                // if was active focus (not hover) when activated, then set focus to next
                if (wasFocused)
                {
                    FocusManager.instance.focused = this.nextFocus;
                }
                this.emit('changed');
            }
            await this.changeCB?.(wasFocused);
            // in theory we could do something here if we needed to
        });

        this.on('focused', async () => {
            if (this.tutorialBlocked)
            {
                this.emit('tutorialBlocked');
                return;
            }
            // if dwell clicks are enabled, do't do anything special on focus
            // - we want to wait for the dwell click to happen
            if (Settings.instance.enableDwellClicks.value || !this.enabled) return;

            const result = await CoordinateKeypad.instance.enableKeyboard(this.allowedLabels);
            if (result === CLEAR)
            {
                this.clear();
            }
            else if (result)
            {
                this.label = result;
                // this.emit('changed');
                // // wait to allow handlers to trigger and finish before we change the focus
                // await wait(5);
                // // if launched via fake event from focus activate(), then set focus to next
                // FocusManager.instance.focused = this.nextFocus;
            }
            // if a change was actually made, do the change callback
            if (result)
            {
                await this.changeCB?.(true);
            }
            // in theory we could do something here if we needed to
        });
    }

    public activate()
    {
        if (this.enabled || CoordinateKeypad.instance.isActive)
        {
            this.emit('pointertap');
        }
    }

    public get focus() { return this.focusBox.visible; }
    public set focus(focused) {
        if (focused === this.focus) return;

        this.focusBox.visible = focused;
        if (focused)
        {
            this.emit('pointerover');
            this.emit('focused');
        }
        else
        {
            this.emit('pointerout');
            this.emit('unfocused');
            if (!CoordinateKeypad.instance.visible)
            {
                CoordinateKeypad.instance.disableKeyboard();
            }
        }
    }

    public get focusVisible() { return this.focusBox.visible; }
    public set focusVisible(visible) {
        this.focusBox.visible = visible;
    }

    public get coord() { return this.label; }

    public clear()
    {
        this.label = '';
    }
}